import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Bar, Line } from "react-chartjs-2";
import { AuthContext } from "../../shared/context/auth-context";
import { openOrSave } from "../../shared/util/fileDownload";
import { CsvButton, KpiCard, Skeleton, downloadCsv, formatDate, formatDateTime, formatRelative } from "./admin/adminUi";
import "./admin/Admin.css";
import "./LiveFund.css";

// /live?mode=paper|live&range=…&table=… — the logged-in user's own live fund:
// the trades the trader placed in their IBKR accounts and how the accounts did
// (backend/util/live-metrics.js). Subscribers download it as a PDF report
// (backend/util/live-report.js): for the period shown, or a calendar month
// (Statements).

const RANGES = [
  { key: "1m",  label: "1M" },
  { key: "3m",  label: "3M" },
  { key: "6m",  label: "6M" },
  { key: "ytd", label: "YTD" },
  { key: "1y",  label: "1Y" },
  { key: "all", label: "All" },
];
const MODES = [
  { key: "paper", label: "Paper" },
  { key: "live",  label: "Live" },
];
const TABLES = [
  { key: "trades",    label: "Trades" },
  { key: "daily",     label: "Daily status" },
  { key: "positions", label: "Positions" },
  { key: "closed",    label: "Closed trades" },
  { key: "statements", label: "Statements" },
];
const SETTINGS = "/usersettings?tab=trading";
const BILLING = "/usersettings?tab=billing";
const EMAILS = "/usersettings?tab=settings";
const PAGE_ROWS = 50;

// ─── Formatting ───────────────────────────────────────────────────────────────

const MINUS = "−";
const signed = (text, v) => (v > 0 ? `+${text}` : text.replace("-", MINUS));

function money(v, currency, digits = 0) {
  if (v == null) return "—";
  const text = new Intl.NumberFormat("en-CH", {
    style: "currency", currency, minimumFractionDigits: digits, maximumFractionDigits: digits,
  }).format(Math.abs(v));
  return v < 0 && text.replace(/[^1-9]/g, "") ? MINUS + text : text;
}
const signedMoney = (v, currency, digits = 0) => (v == null ? "—" : v > 0 ? `+${money(v, currency, digits)}` : money(v, currency, digits));
const pct = (v, digits = 2) => (v == null ? "—" : signed(`${(v * 100).toFixed(digits)} %`, v));
const share = (v, digits = 1) => (v == null ? "—" : `${(v * 100).toFixed(digits)} %`);
// Position weights: one decimal, two significant digits under 1 % so that a
// few shares in the model fund (millions) don't read 0.0 %, and 0 as 0.00 %
const weight = (v) => {
  if (v == null) return "—";
  const p = Math.abs(v * 100);
  return share(v, p === 0 ? 2 : p < 1 ? Math.min(6, 1 - Math.floor(Math.log10(p))) : 1);
};
const ratio = (v) => (v == null ? "—" : v.toFixed(2).replace("-", MINUS));
const bps = (v) => (v == null ? "—" : signed(`${Math.round(v)} bps`, Math.round(v)));
const price = (v, currency) => (v == null ? "—" : v.toLocaleString("en-CH", {
  minimumFractionDigits: currency === "JPY" ? 0 : 2, maximumFractionDigits: currency === "JPY" ? 0 : 2,
}));
const qty = (v) => (v == null ? "—" : Number(v).toLocaleString("en-CH", { maximumFractionDigits: 4 }));
const tone = (v) => (v == null || v === 0 ? "" : v > 0 ? "adm-text-accent" : "adm-text-danger");
const shortDay = (day) => new Date(`${day}T12:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
const monthName = (month) => new Date(`${month}-15T12:00:00Z`).toLocaleDateString("en-GB", { month: "long", year: "numeric", timeZone: "UTC" });
const longDay = (day) => (day ? formatDate(`${day}T12:00:00Z`) : "—");

// ─── Chips ────────────────────────────────────────────────────────────────────

const ORDER_STATUS = {
  filled:           { label: "Filled",       tone: "good" },
  partially_filled: { label: "Partly filled", tone: "info" },
  submitted:        { label: "Working",      tone: "info" },
  planned:          { label: "Planned",      tone: "dim" },
  expired:          { label: "Expired",      tone: "dim" },
  rejected:         { label: "Rejected",     tone: "bad" },
  cancelled:        { label: "Cancelled",    tone: "dim" },
  unsent:           { label: "Not sent",     tone: "dim" },
  dry_run:          { label: "Dry run",      tone: "warn" },
};
const KIND_HELP = {
  BUY: "Buy signal", "RE-BUY": "Adds to a position after a re-buy signal", ENTRY: "Buys into a trade the model holds, on its re-buy signal",
  SELL: "Sell signal", EXIT: "The model no longer holds the position",
};

function Chip({ tone: t, children, title }) {
  return <span className={`adm-chip lf-tone--${t}`} title={title}>{children}</span>;
}

function OrderChip({ status }) {
  const s = ORDER_STATUS[status] || { label: status, tone: "dim" };
  return <Chip tone={s.tone}>{s.label}</Chip>;
}

function RunChip({ run, label }) {
  if (!run) return <span className="adm-off">—</span>;
  const failed = run.status === "failed";
  const title = [label, run.at && formatDateTime(run.at), run.attempts > 1 && `${run.attempts} attempts`, run.error]
    .filter(Boolean).join(" · ");
  return <Chip tone={failed ? "bad" : "good"} title={title}>{failed ? (run.loginFailed ? "Login failed" : "Failed") : "OK"}</Chip>;
}

// ─── Charts ───────────────────────────────────────────────────────────────────

// On white cards like /portfolio (components/BacktestOverview.js): the
// account in the accent a step darker (#00d97e has too little contrast on
// white), the model fund in blue, gains and losses in blue and red; checked
// against white (lightness, contrast, colour-blind separation)
const LIGHT = { account: "#00a86b", model: "#0096e6", gain: "#0096e6", loss: "#ff4757", ticks: "#888888", grid: "#ececec", legend: "#555555" };

const axis = {
  ticks: { color: LIGHT.ticks, font: { size: 10 }, maxRotation: 0, autoSkipPadding: 12 },
  grid: { color: LIGHT.grid },
  border: { display: false },
};
const percentAxis = { ...axis, ticks: { ...axis.ticks, callback: (v) => `${Math.round(v * 1000) / 10} %` } };
const baseOptions = (extra = {}) => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: false,
  interaction: { mode: "index", intersect: false },
  plugins: { legend: { labels: { color: LIGHT.legend, boxWidth: 10, font: { size: 11 } } } },
  ...extra,
});
const pctTooltip = { callbacks: { label: (c) => `${c.dataset.label}: ${pct(c.parsed.y ?? c.parsed.x)}` } };

function PerformanceChart({ series }) {
  const hasModel = series.some((p) => p.model != null);
  return (
    <Line
      data={{
        labels: series.map((p) => shortDay(p.day)),
        datasets: [
          { label: "Your account", data: series.map((p) => p.cum), borderColor: LIGHT.account, backgroundColor: "rgba(0, 168, 107, 0.10)",
            borderWidth: 2, pointRadius: 0, tension: 0.15, fill: "origin" },
          ...(hasModel ? [{ label: "Model fund", data: series.map((p) => p.model), borderColor: LIGHT.model, backgroundColor: LIGHT.model, borderWidth: 1.5,
            borderDash: [4, 3], pointRadius: 0, tension: 0.15, fill: false }] : []),
        ],
      }}
      options={baseOptions({ plugins: { ...baseOptions().plugins, tooltip: pctTooltip }, scales: { x: axis, y: percentAxis } })}
    />
  );
}

function DrawdownChart({ series }) {
  return (
    <Line
      data={{
        labels: series.map((p) => shortDay(p.day)),
        datasets: [{ label: "Drawdown", data: series.map((p) => p.drawdown), borderColor: LIGHT.loss,
          backgroundColor: "rgba(255, 71, 87, 0.15)", borderWidth: 1.5, pointRadius: 0, tension: 0.1, fill: "origin" }],
      }}
      options={baseOptions({ plugins: { legend: { display: false }, tooltip: pctTooltip }, scales: { x: axis, y: { ...percentAxis, max: 0 } } })}
    />
  );
}

function DailyReturnsChart({ series }) {
  const days = series.filter((p) => p.ret != null);
  return (
    <Bar
      data={{
        labels: days.map((p) => shortDay(p.day)),
        datasets: [{ label: "Daily return", data: days.map((p) => p.ret),
          backgroundColor: days.map((p) => (p.ret >= 0 ? LIGHT.gain : LIGHT.loss)), borderRadius: 1 }],
      }}
      options={baseOptions({ plugins: { legend: { display: false }, tooltip: pctTooltip }, scales: { x: axis, y: percentAxis } })}
    />
  );
}

// The account's positions next to the model fund's, including what only the
// model holds (e.g. a lot too large for the account)
function allocationRows(positions, modelWeights) {
  const rows = positions.filter((p) => p.weight != null).map((p) => ({ ticker: p.ticker, weight: p.weight, model: p.modelWeight }));
  const held = new Set(rows.map((r) => r.ticker));
  Object.entries(modelWeights || {}).forEach(([ticker, model]) => {
    if (!held.has(ticker) && model > 0) rows.push({ ticker, weight: 0, model });
  });
  return rows.sort((a, b) => Math.max(b.weight, b.model ?? 0) - Math.max(a.weight, a.model ?? 0));
}

// A held position is a bar of at least 3 px, however small its weight; a
// weight of 0 is no bar (chart.js would draw minBarLength at 0 too), and the
// tooltip reads the exact weights. Every share in this chart has two decimals
const allocationBars = (label, weights, color) => ({
  label, weights, data: weights.map((w) => w || null), backgroundColor: color, borderRadius: 2, minBarLength: 3,
});

function AllocationChart({ rows }) {
  const hasModel = rows.some((r) => r.model != null);
  return (
    <Bar
      data={{
        labels: rows.map((r) => r.ticker),
        datasets: [
          allocationBars("Your account", rows.map((r) => r.weight), LIGHT.account),
          ...(hasModel ? [allocationBars("Model fund", rows.map((r) => r.model), LIGHT.model)] : []),
        ],
      }}
      options={baseOptions({
        indexAxis: "y",
        interaction: { mode: "index", intersect: false, axis: "y" },
        plugins: { ...baseOptions().plugins, tooltip: { callbacks: { label: (c) => `${c.dataset.label}: ${share(c.dataset.weights[c.dataIndex], 2)}` } } },
        scales: {
          x: { ...axis, ticks: { ...axis.ticks, callback: (v) => share(v, 2) }, beginAtZero: true },
          y: { ...axis, grid: { display: false }, ticks: { ...axis.ticks, autoSkip: false, font: { size: 9 } } },
        },
      })}
    />
  );
}

// ─── Tables ───────────────────────────────────────────────────────────────────

// columns: [{ key, label, numeric, value(row) → sort and CSV value, render(row) }]
function DataTable({ columns, rows, initialSort, empty, rowKey, renderDetail }) {
  const [sort, setSort] = useState(initialSort);
  const [open, setOpen] = useState(null);
  const [shown, setShown] = useState(PAGE_ROWS);
  const sorted = useMemo(() => {
    const column = columns.find((c) => c.key === sort.key);
    if (!column) return rows;
    const value = column.value || ((r) => r[column.key]);
    return [...rows].sort((a, b) => {
      const x = value(a), y = value(b);
      if (x == null && y == null) return 0;
      if (x == null) return 1;
      if (y == null) return -1;
      return (x < y ? -1 : x > y ? 1 : 0) * (sort.dir === "asc" ? 1 : -1);
    });
  }, [columns, rows, sort]);

  const toggle = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));
  return (
    <div className="adm-table-wrap">
      <table className={`adm-table ${renderDetail ? "" : "adm-table--static"}`}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} scope="col" className={c.numeric ? "is-numeric" : ""}
                  aria-sort={sort.key === c.key ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
                <button type="button" onClick={() => toggle(c.key)} title={c.help}>
                  {c.label}{sort.key === c.key ? (sort.dir === "asc" ? " ▲" : " ▼") : ""}
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {!sorted.length && <tr><td colSpan={columns.length} className="adm-empty">{empty}</td></tr>}
          {sorted.slice(0, shown).map((row) => {
            const key = rowKey(row);
            const detail = renderDetail ? renderDetail(row) : null;
            return (
              <React.Fragment key={key}>
                <tr tabIndex={detail ? 0 : undefined} aria-expanded={detail ? open === key : undefined}
                    className={detail ? "" : "lf-row--plain"}
                    onClick={detail ? () => setOpen(open === key ? null : key) : undefined}
                    onKeyDown={detail ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(open === key ? null : key); } } : undefined}>
                  {columns.map((c) => (
                    <td key={c.key} className={c.numeric ? "is-numeric" : c.wrap ? "adm-wrap" : ""}>
                      {c.render ? c.render(row) : row[c.key] ?? "—"}
                    </td>
                  ))}
                </tr>
                {detail && open === key && (
                  <tr className="lf-detail"><td colSpan={columns.length}>{detail}</td></tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      {sorted.length > shown && (
        <div className="lf-more">
          <span className="adm-note">{shown} of {sorted.length}</span>
          <button type="button" className="adm-btn adm-btn--outline adm-btn--small" onClick={() => setShown((n) => n + PAGE_ROWS * 4)}>Show more</button>
          <button type="button" className="adm-btn adm-btn--outline adm-btn--small" onClick={() => setShown(sorted.length)}>Show all</button>
        </div>
      )}
    </div>
  );
}

const csvOf = (columns, rows) => [columns.map((c) => c.csvLabel || c.label), rows.map((r) => columns.map((c) => {
  const value = c.csv ? c.csv(r) : c.value ? c.value(r) : r[c.key];
  return typeof value === "number" ? Math.round(value * 1e6) / 1e6 : value;
}))];

function tradeColumns(currency) {
  return [
    { key: "day", label: "Day", render: (r) => longDay(r.day), help: "The morning the order was sent" },
    { key: "ticker", label: "Ticker", render: (r) => <span className="adm-strong">{r.ticker}</span> },
    { key: "kind", label: "Type", render: (r) => <span title={KIND_HELP[r.kind]}>{r.kind}</span> },
    { key: "side", label: "Side", render: (r) => <span className={r.side === "BUY" ? "adm-text-accent" : "adm-text-danger"}>{r.side}</span> },
    { key: "quantity", label: "Quantity", numeric: true, render: (r) => (r.filledQuantity && r.filledQuantity !== r.quantity
      ? <span title="filled of ordered">{qty(r.filledQuantity)} / {qty(r.quantity)}</span> : qty(r.quantity)) },
    { key: "close", label: "Signal close", numeric: true, render: (r) => price(r.close, r.currency), help: "The close the order was planned on" },
    { key: "limit", label: "Limit", numeric: true, render: (r) => price(r.limit, r.currency) },
    { key: "avgPrice", label: "Fill", numeric: true, render: (r) => price(r.avgPrice, r.currency) },
    { key: "currency", label: "Ccy" },
    { key: "value", label: `Value ${currency}`, numeric: true, value: (r) => r.value ?? r.plannedValue,
      render: (r) => (r.value != null ? money(r.value, currency) : <span className="adm-off" title="planned">{money(r.plannedValue, currency)}</span>) },
    { key: "weight", label: "Size", numeric: true, render: (r) => share(r.weight), help: "Share of the account value the order was sized for" },
    { key: "shortfallBps", label: "Shortfall", numeric: true, help: "Fill against the signal close; positive is a cost",
      render: (r) => <span className={tone(r.shortfallBps == null ? null : -r.shortfallBps)}>{bps(r.shortfallBps)}</span> },
    { key: "commission", label: `Fees ${currency}`, numeric: true, render: (r) => money(r.commission, currency, 2) },
    { key: "status", label: "Status", render: (r) => (
      <>
        <OrderChip status={r.status} />
        {r.inferred && <span className="adm-tag adm-tag--warn" title="Taken from the change of the position; IBKR no longer reported the fill">inferred</span>}
      </>
    ) },
  ];
}

function TradesTable({ data }) {
  const [filter, setFilter] = useState("all");
  const columns = useMemo(() => tradeColumns(data.currency), [data.currency]);
  const counts = useMemo(() => {
    const c = { all: data.trades.length };
    for (const t of data.trades) {
      const group = t.status === "partially_filled" ? "filled" : t.status === "planned" ? "submitted" : t.status;
      c[group] = (c[group] || 0) + 1;
    }
    return c;
  }, [data.trades]);
  const filters = [["all", "All"], ["filled", "Filled"], ["submitted", "Working"], ["expired", "Expired"], ["rejected", "Rejected"],
    ["cancelled", "Cancelled"], ["unsent", "Not sent"], ["dry_run", "Dry runs"]]
    .filter(([key]) => key === "all" || counts[key]);
  const rows = data.trades.filter((t) => filter === "all"
    || (filter === "filled" ? ["filled", "partially_filled"].includes(t.status)
      : filter === "submitted" ? ["submitted", "planned"].includes(t.status) : t.status === filter));

  const exportCsv = () => {
    const extra = [
      { key: "signalDate", label: "Signal day" }, { key: "tas", label: "TAS" }, { key: "id", label: "Order id" },
      { key: "error", label: "Error" },
    ];
    const [header, body] = csvOf([...columns, ...extra], rows);
    downloadCsv(`mtt-live-${data.mode}-trades.csv`, header, body);
  };
  return (
    <>
      <div className="adm-list-head">
        <div className="adm-filters" role="group" aria-label="Order status">
          {filters.map(([key, label]) => (
            <button key={key} type="button" className={`adm-filter ${filter === key ? "is-active" : ""}`}
                    aria-pressed={filter === key} onClick={() => setFilter(key)}>
              {label}<span>{counts[key] || 0}</span>
            </button>
          ))}
        </div>
        <CsvButton onClick={exportCsv} />
      </div>
      <DataTable columns={columns} rows={rows} initialSort={{ key: "day", dir: "desc" }} rowKey={(r) => r.id}
                 empty="No orders in this period."
                 renderDetail={(r) => (r.error || r.signalDate ? (
                   <dl className="lf-facts">
                     <div><dt>Signal day</dt><dd>{longDay(r.signalDate)}</dd></div>
                     <div><dt>TAS</dt><dd>{r.tas == null ? "—" : r.tas.toFixed(2)}</dd></div>
                     <div><dt>Sent</dt><dd>{formatDateTime(r.submittedAt)}</dd></div>
                     <div><dt>Settled</dt><dd>{formatDateTime(r.settledAt)}</dd></div>
                     {r.error && <div className="lf-facts__wide"><dt>IBKR</dt><dd className="adm-text-danger">{r.error}</dd></div>}
                   </dl>
                 ) : null)} />
      {data.tradesTruncated && <p className="adm-note">Showing the newest {data.trades.length} orders; export covers the same.</p>}
    </>
  );
}

function runSummary(run) {
  if (!run) return null;
  const parts = [];
  if (run.sent != null) parts.push(`${run.sent} sent`);
  else if (run.planned != null) parts.push(`${run.planned} planned`);
  if (run.settled) parts.push(`${run.settled} settled`);
  if (run.skipped?.length) parts.push(`${run.skipped.length} skipped`);
  if (run.dryRun) parts.push("dry run");
  return parts.join(" · ");
}

function DailyTable({ data }) {
  const { currency } = data;
  const columns = [
    { key: "day", label: "Day", render: (r) => (
      <>
        {longDay(r.day)}
        {r.provisional && <span className="adm-tag adm-tag--warn" title="From the morning run; the evening run records the close">intraday</span>}
      </>
    ) },
    { key: "nav", label: "Account value", numeric: true, render: (r) => money(r.nav, currency) },
    { key: "ret", label: "Return", numeric: true, render: (r) => <span className={tone(r.ret)}>{pct(r.ret)}</span> },
    { key: "change", label: `P&L ${currency}`, numeric: true, render: (r) => <span className={tone(r.change)}>{signedMoney(r.change, currency)}</span> },
    { key: "place", label: "Morning run", value: (r) => r.place?.status, render: (r) => (
      <span className="lf-run"><RunChip run={r.place} label="Morning run: orders" /><small>{runSummary(r.place)}</small></span>
    ) },
    { key: "reconcile", label: "Evening run", value: (r) => r.reconcile?.status, render: (r) => (
      <span className="lf-run"><RunChip run={r.reconcile} label="Evening run: fills" /><small>{runSummary(r.reconcile)}</small></span>
    ) },
    { key: "orders", label: "Orders", numeric: true, render: (r) => (r.orders ? (
      <span title={`${r.filled} filled · ${r.expired} expired · ${r.rejected} rejected · ${r.cancelled} cancelled · ${r.open} open`}>
        {r.orders}{r.filled !== r.orders && <small className="adm-off"> ({r.filled} filled)</small>}
      </span>
    ) : "—") },
    { key: "tradedValue", label: `Traded ${currency}`, numeric: true, render: (r) => (r.tradedValue ? money(r.tradedValue, currency) : "—") },
    { key: "commissions", label: `Fees ${currency}`, numeric: true, render: (r) => (r.commissions ? money(r.commissions, currency, 2) : "—") },
  ];
  const exportCsv = () => {
    downloadCsv(`mtt-live-${data.mode}-daily.csv`,
      ["Day", `Account value ${currency}`, "Return", `P&L ${currency}`, "Morning run", "Morning orders sent", "Skipped", "Morning error",
        "Evening run", "Evening error", "Orders", "Filled", "Expired", "Rejected", `Traded ${currency}`, `Fees ${currency}`, "Intraday"],
      data.daily.map((r) => [r.day, r.nav, r.ret, r.change, r.place?.status, r.place?.sent, r.place?.skipped?.length, r.place?.error,
        r.reconcile?.status, r.reconcile?.error, r.orders, r.filled, r.expired, r.rejected, r.tradedValue, r.commissions, r.provisional]));
  };
  return (
    <>
      <div className="adm-list-head">
        <span className="adm-note">Morning run: orders for the day (07:00). Evening run: fills and the closing value (23:00). Click a day for details.</span>
        <CsvButton onClick={exportCsv} />
      </div>
      <DataTable columns={columns} rows={data.daily} initialSort={{ key: "day", dir: "desc" }} rowKey={(r) => r.day}
                 empty="Nothing recorded in this period."
                 renderDetail={(r) => {
                   const notes = [];
                   if (r.place?.error) notes.push(<p key="pe" className="adm-text-danger">Morning run: {r.place.error}</p>);
                   if (r.reconcile?.error) notes.push(<p key="re" className="adm-text-danger">Evening run: {r.reconcile.error}</p>);
                   if (r.place?.signalDate) notes.push(<p key="sd" className="adm-note">Signals of {longDay(r.place.signalDate)}{r.place.attempts > 1 ? ` · ${r.place.attempts} attempts` : ""}</p>);
                   if (r.place?.skipped?.length) {
                     notes.push(
                       <ul key="sk" className="lf-skipped">
                         {r.place.skipped.map((s, i) => <li key={i}><span className="adm-strong">{s.kind} {s.ticker}</span> not sent: {s.reason}</li>)}
                       </ul>
                     );
                   }
                   return notes.length ? <div className="lf-detail__body">{notes}</div> : null;
                 }} />
    </>
  );
}

function PositionsTable({ data }) {
  const { currency } = data;
  const columns = [
    { key: "ticker", label: "Ticker", render: (r) => <span className="adm-strong">{r.ticker}</span> },
    { key: "quantity", label: "Quantity", numeric: true, render: (r) => qty(r.quantity) },
    { key: "price", label: "Price", numeric: true, render: (r) => price(r.price, r.currency) },
    { key: "currency", label: "Ccy" },
    { key: "value", label: `Value ${currency}`, numeric: true, render: (r) => money(r.value, currency) },
    { key: "weight", label: "Weight", numeric: true, render: (r) => weight(r.weight) },
    { key: "modelWeight", label: "Model", numeric: true, render: (r) => weight(r.modelWeight), help: "The model fund's weight" },
    { key: "bookValue", label: `Cost ${currency}`, numeric: true, render: (r) => money(r.bookValue, currency), help: "Everything bought, re-buys included" },
    { key: "unrealized", label: `P&L ${currency}`, numeric: true, render: (r) => <span className={tone(r.unrealized)}>{signedMoney(r.unrealized, currency)}</span> },
    { key: "unrealizedPct", label: "P&L %", numeric: true, render: (r) => <span className={tone(r.unrealizedPct)}>{pct(r.unrealizedPct)}</span> },
    { key: "holdingDays", label: "Held", numeric: true, render: (r) => (r.holdingDays == null ? "—" : `${r.holdingDays} d`) },
    { key: "openedAt", label: "Since", value: (r) => r.openedAt, render: (r) => formatDate(r.openedAt) },
  ];
  const exportCsv = () => {
    const [header, body] = csvOf([...columns, { key: "tradeId", label: "Trade" }, { key: "entryValue", label: `Entry ${currency}` }], data.positions);
    downloadCsv(`mtt-live-${data.mode}-positions.csv`, header, body);
  };
  return (
    <>
      <div className="adm-list-head">
        <span className="adm-note">What MTT holds in the account, valued at IBKR's prices{data.summary?.asOf ? ` of ${longDay(data.summary.asOf)}` : ""}.</span>
        <CsvButton onClick={exportCsv} />
      </div>
      <DataTable columns={columns} rows={data.positions} initialSort={{ key: "value", dir: "desc" }} rowKey={(r) => r.ticker}
                 empty="No open positions." />
    </>
  );
}

function ClosedTable({ data }) {
  const { currency } = data;
  const columns = [
    { key: "ticker", label: "Ticker", render: (r) => <span className="adm-strong">{r.ticker}</span> },
    { key: "openedAt", label: "Opened", render: (r) => formatDate(r.openedAt) },
    { key: "closedAt", label: "Closed", render: (r) => formatDate(r.closedAt) },
    { key: "holdingDays", label: "Held", numeric: true, render: (r) => (r.holdingDays == null ? "—" : `${r.holdingDays} d`) },
    { key: "bookValue", label: `Cost ${currency}`, numeric: true, render: (r) => money(r.bookValue, currency) },
    { key: "proceeds", label: `Proceeds ${currency}`, numeric: true, render: (r) => money(r.proceeds, currency) },
    { key: "pnl", label: `P&L ${currency}`, numeric: true, render: (r) => <span className={tone(r.pnl)}>{signedMoney(r.pnl, currency)}</span> },
    { key: "returnPct", label: "Return", numeric: true, render: (r) => <span className={tone(r.returnPct)}>{pct(r.returnPct)}</span> },
  ];
  const exportCsv = () => {
    const [header, body] = csvOf([...columns, { key: "tradeId", label: "Trade" }], data.closedTrades);
    downloadCsv(`mtt-live-${data.mode}-closed-trades.csv`, header, body);
  };
  return (
    <>
      <div className="adm-list-head">
        <span className="adm-note">Positions sold completely in this period; P&L after all buys, before fees.</span>
        <CsvButton onClick={exportCsv} />
      </div>
      <DataTable columns={columns} rows={data.closedTrades} initialSort={{ key: "closedAt", dir: "desc" }}
                 rowKey={(r) => `${r.ticker}-${r.closedAt}`} empty="No trades closed in this period." />
    </>
  );
}

// One PDF per calendar month, and one since the start. busy: the row whose
// report is loading ("<key>:view" / "<key>:save").
function StatementsTable({ data, account, paid, busy, onOpen }) {
  const rows = [
    ...(account?.hasData ? [{ key: "all", query: { range: "all" }, label: "Since the start", from: account.firstDay,
      to: account.lastDay, days: null, ret: account.totalReturn, model: null, complete: true }] : []),
    ...(data.statements || []).map((m) => ({ key: m.month, query: { month: m.month }, label: monthName(m.month),
      from: m.firstDay, to: m.lastDay, days: m.days, ret: m.totalReturn, model: m.modelReturn, complete: m.complete })),
  ];
  return (
    <>
      <div className="adm-list-head">
        <span className="adm-note">
          A PDF report of the {data.mode} account for each month and since the start: performance against the model fund and the
          S&P 500, the positions, closed trades, every fill and the fees.
          {paid && <> Each month's statement can also come by email: <Link to={EMAILS}>email settings</Link>.</>}
        </span>
      </div>
      {!paid && <LockedNotice />}
      <div className="adm-table-wrap">
        <table className="adm-table adm-table--static lf-statements">
          <thead>
            <tr>
              <th scope="col">Statement</th>
              <th scope="col">Covers</th>
              <th scope="col" className="is-numeric">Daily values</th>
              <th scope="col" className="is-numeric">Return</th>
              <th scope="col" className="is-numeric">Model fund</th>
              <th scope="col" className="is-numeric"><span className="lf-hidden">Report</span></th>
            </tr>
          </thead>
          <tbody>
            {!rows.length && <tr><td colSpan={6} className="adm-empty">No account values yet: they're recorded after each daily run.</td></tr>}
            {rows.map((r) => (
              <tr key={r.key}>
                <td>
                  <span className="adm-strong">{r.label}</span>
                  {!r.complete && <span className="adm-tag adm-tag--warn" title="The month isn't over yet">to date</span>}
                </td>
                <td>{longDay(r.from)} – {longDay(r.to)}</td>
                <td className="is-numeric">{r.days ?? "—"}</td>
                <td className="is-numeric"><span className={tone(r.ret)}>{pct(r.ret)}</span></td>
                <td className="is-numeric adm-off">{pct(r.model)}</td>
                <td className="is-numeric">
                  {paid ? (
                    <>
                      <button type="button" className="lf-link" disabled={Boolean(busy)} onClick={() => onOpen(r.query, true, `${r.key}:view`)}
                              aria-label={`View the report: ${r.label}`}>
                        {busy === `${r.key}:view` ? "Opening…" : "View"}
                      </button>
                      <button type="button" className="lf-link" disabled={Boolean(busy)} onClick={() => onOpen(r.query, false, `${r.key}:save`)}
                              aria-label={`Download the report: ${r.label}`}>
                        {busy === `${r.key}:save` ? "Preparing…" : "Download"}
                      </button>
                    </>
                  ) : <span className="adm-off">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ─── Panels ───────────────────────────────────────────────────────────────────

function LockIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M4 7V5a4 4 0 1 1 8 0v2h1a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1h1zm2 0h4V5a2 2 0 1 0-4 0v2z" />
    </svg>
  );
}

function LockedNotice() {
  return (
    <ul className="lf-notices">
      <li className="lf-notice lf-notice--info">
        Account reports are part of the subscription: a PDF of your account for any period, and a statement for every month.
        <Link to={BILLING}>View plan</Link>
      </li>
    </ul>
  );
}

function AccountCard({ account, active, onSelect }) {
  const label = account.mode === "live" ? "Live account" : "Paper account";
  const run = account.lastRun;
  return (
    <button type="button" className={`lf-account ${active ? "is-active" : ""}`} aria-pressed={active} onClick={onSelect}>
      <span className="lf-account__head">
        <span className="adm-kpi__label">{label}</span>
        {account.autoTrading ? <Chip tone="good">Automatic trading on</Chip>
          : account.connected ? <Chip tone="dim">Automatic trading off</Chip> : <Chip tone="dim">Not connected</Chip>}
      </span>
      {account.hasData ? (
        <>
          <span className="lf-account__value">{money(account.nav, account.currency)}</span>
          <span className="adm-kpi__sub">
            <span className={tone(account.totalReturn)}>{pct(account.totalReturn)}</span> since {longDay(account.firstDay)}
            {" · "}{share(account.investedRatio, 0)} invested · {account.positions} position{account.positions === 1 ? "" : "s"}
          </span>
          <span className="adm-kpi__sub lf-account__meta">
            {account.account} · {account.currency}
            {run && <> · {run.kind === "place" ? "morning" : "evening"} run {run.status === "failed"
              ? <span className="adm-text-danger">failed</span> : "OK"} {formatRelative(run.at)}</>}
          </span>
        </>
      ) : (
        <span className="adm-kpi__sub lf-account__empty">No trades yet</span>
      )}
    </button>
  );
}

function Notices({ account }) {
  const items = [];
  const run = account?.lastRun;
  if (run?.status === "failed") {
    items.push({
      tone: "bad",
      text: <>The last {run.kind === "place" ? "morning" : "evening"} run ({formatDateTime(run.at)}) failed{run.error ? `: ${run.error}` : "."}
        {" "}{/login/i.test(run.error || "") && <Link to={SETTINGS}>Check your IBKR login</Link>}</>,
    });
  }
  if (account?.hasData && !account.autoTrading) {
    items.push({ tone: "info", text: <>Automatic trading is off for this account: no new orders are placed. <Link to={SETTINGS}>Turn it on</Link></> });
  }
  if (!items.length) return null;
  return (
    <ul className="lf-notices">
      {items.map((n, i) => <li key={i} className={`lf-notice lf-notice--${n.tone}`} role={n.tone === "bad" ? "alert" : undefined}>{n.text}</li>)}
    </ul>
  );
}

function StatsTable({ s }) {
  const m = s.model;
  const rows = [
    ["Total return", pct(s.totalReturn), m && pct(m.totalReturn), "Time-weighted, from the daily account values"],
    ["Annualized", pct(s.annualizedReturn), m && pct(m.annualizedReturn), "From 90 days of history"],
    ["Volatility", share(s.volatility, 2), m && share(m.volatility, 2), "Annualized standard deviation of daily returns"],
    ["Sharpe ratio", ratio(s.sharpe), null, "Return per unit of volatility (risk-free rate 0)"],
    ["Sortino ratio", ratio(s.sortino), null, "Return per unit of downside volatility"],
    ["Max drawdown", pct(s.maxDrawdown), m && pct(m.maxDrawdown), "Largest fall from a high"],
    ["Calmar ratio", ratio(s.calmar), null, "Annualized return / max drawdown"],
    ["Beta", ratio(s.beta), null, "Sensitivity to the model fund's daily moves"],
    ["Correlation", ratio(s.correlation), null, "With the model fund's daily returns"],
    ["Tracking error", share(s.trackingError, 2), null, "Annualized volatility of the difference to the model"],
    ["Information ratio", ratio(s.informationRatio), null, "Excess return per unit of tracking error"],
    ["Positive days", share(s.positiveDays, 0), null, null],
    ["Best day", s.bestDay ? `${pct(s.bestDay.ret)} · ${shortDay(s.bestDay.day)}` : "—", null, null],
    ["Worst day", s.worstDay ? `${pct(s.worstDay.ret)} · ${shortDay(s.worstDay.day)}` : "—", null, null],
  ];
  return (
    <table className="lf-stats">
      <thead>
        <tr><th scope="col" /><th scope="col">Account</th>{m && <th scope="col">Model</th>}</tr>
      </thead>
      <tbody>
        {rows.map(([label, value, model, help]) => (
          <tr key={label}>
            <th scope="row" title={help || undefined}>{label}</th>
            <td>{value}</td>
            {m && <td className="adm-off">{model ?? ""}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Execution({ t, currency }) {
  const kinds = Object.entries(t.byKind || {});
  return (
    <dl className="lf-facts lf-facts--stack">
      <div><dt>Orders</dt><dd>{t.orders}{t.open ? <small className="adm-off"> · {t.open} working</small> : null}</dd></div>
      <div><dt>Fill rate</dt><dd>{share(t.fillRate, 0)}<small className="adm-off"> · {t.byStatus?.expired || 0} expired · {t.byStatus?.rejected || 0} rejected</small></dd></div>
      <div><dt>Traded</dt><dd>{money(t.tradedValue, currency)}<small className="adm-off"> · buys {money(t.buyValue, currency)} · sells {money(t.sellValue, currency)}</small></dd></div>
      <div><dt>Turnover</dt><dd title="Traded value / average account value">{t.turnover == null ? "—" : `${t.turnover.toFixed(2)}×`}</dd></div>
      <div><dt>Fees</dt><dd>{money(t.commissions, currency, 2)}<small className="adm-off"> · {t.commissionBps == null ? "—" : `${t.commissionBps.toFixed(1)} bps`}</small></dd></div>
      <div><dt title="Fills against the signal close, weighted by value; positive is a cost">Shortfall</dt><dd className={tone(t.shortfallBps == null ? null : -t.shortfallBps)}>{bps(t.shortfallBps)}</dd></div>
      {kinds.length > 0 && (
        <div className="lf-facts__wide"><dt>By type</dt><dd>{kinds.map(([k, n]) => <span key={k} className="adm-count lf-kind" title={KIND_HELP[k]}>{k} {n}</span>)}</dd></div>
      )}
    </dl>
  );
}

function LiveSkeleton() {
  return (
    <div aria-busy="true">
      <div className="lf-card"><div className="lf-accounts">{[0, 1].map((i) => <div key={i} className="lf-account"><Skeleton className="adm-skeleton--label" /><Skeleton className="adm-skeleton--value" /></div>)}</div></div>
      <div className="lf-card"><div className="adm-kpis">{[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="adm-kpi"><Skeleton className="adm-skeleton--label" /><Skeleton className="adm-skeleton--value" /></div>)}</div></div>
      <div className="lf-card"><Skeleton className="adm-skeleton--chart" /></div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="lf-card adm-message lf-empty">
      <h2 className="adm-panel__title">No trades yet</h2>
      <p>
        With automatic trading on, MTT places the model fund's orders in your IBKR account every weekday morning,
        sized to your account. This page then shows each trade, the daily runs and how your account performs
        against the model fund.
      </p>
      <Link to={SETTINGS} className="adm-btn">Set up automatic trading</Link>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const LiveFund = () => {
  const auth = useContext(AuthContext);
  const [params, setParams] = useSearchParams();
  const range = RANGES.some((r) => r.key === params.get("range")) ? params.get("range") : "3m";
  const requestedMode = MODES.some((m) => m.key === params.get("mode")) ? params.get("mode") : null;
  const table = TABLES.some((t) => t.key === params.get("table")) ? params.get("table") : "trades";
  const [loaded, setLoaded] = useState(null); // { key, data }
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [reportBusy, setReportBusy] = useState(null);   // "header", or a statement's "<key>:view|save"
  const [reportError, setReportError] = useState(null);
  const [showLocked, setShowLocked] = useState(false);
  const paid = auth.isPaid || auth.role === "admin";
  const key = `${range}:${requestedMode}`;
  const data = loaded?.data;

  const update = (changes) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([k, v]) => next.set(k, v));
    setParams(next, { replace: true });
  };

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      setError(null);
      try {
        const query = new URLSearchParams({ range, ...(requestedMode ? { mode: requestedMode } : {}) });
        const res = await fetch(`/api/users/me/live?${query}`, {
          headers: { Authorization: "Bearer " + auth.token },
          signal: controller.signal,
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.message || `HTTP ${res.status}`);
        setLoaded({ key: `${range}:${requestedMode}`, data: body });
      } catch (err) {
        if (err.name !== "AbortError") setError(err.message);
      }
    })();
    return () => controller.abort();
  }, [auth.token, range, requestedMode, reloadKey]);

  const mode = data?.mode || requestedMode || "paper";
  const account = data?.accounts?.[mode];
  const s = data?.summary;
  const currency = data?.currency || "CHF";
  const series = useMemo(() => data?.series || [], [data]);
  const navSpark = useMemo(() => series.map((p) => p.nav).filter((v) => v != null), [series]);
  const allocation = useMemo(() => allocationRows(data?.positions || [], data?.modelWeights), [data]);
  const stale = loaded && loaded.key !== key;  // the new period or account is loading
  const hasAny = data && data.modes.length > 0;

  // The PDF report (backend/util/live-report.js). query: { range } or { month }
  const openReport = async (query, view, busyKey) => {
    setReportBusy(busyKey);
    setReportError(null);
    try {
      const params = new URLSearchParams({ mode, ...query, ...(view ? { inline: "1" } : {}) });
      await openOrSave({
        path: `/api/users/me/live/report?${params}`, token: auth.token, view,
        filename: `MTT_account_report_${mode}.pdf`, errorMessage: "Your report couldn't be created.",
      });
    } catch (err) {
      setReportError(err.message);
    } finally {
      setReportBusy(null);
    }
  };
  const rangeLabel = RANGES.find((r) => r.key === range)?.label;
  const reportTitle = !paid ? "Part of the subscription"
    : !s ? "Available after the first daily account value"
      : `PDF report of the ${mode} account for the period shown (${rangeLabel})`;

  return (
    <div className="adm-page lf-page">
      <header className="adm-header lf-card">
        <div>
          <h1>Live fund</h1>
          <p className="adm-note">The model fund's trades in your own IBKR accounts</p>
        </div>
        <div className="lf-header-actions">
          <nav className="adm-tabs" aria-label="Account">
            {MODES.map((m) => (
              <button key={m.key} type="button" aria-current={mode === m.key ? "page" : undefined}
                      className={mode === m.key ? "is-active" : ""} onClick={() => update({ mode: m.key })}>
                {m.label}
              </button>
            ))}
          </nav>
          <button type="button" className="adm-btn adm-btn--outline lf-report-btn" title={reportTitle}
                  disabled={reportBusy === "header" || (paid && !s)}
                  onClick={() => (paid ? openReport({ range }, false, "header") : setShowLocked((v) => !v))}>
            {!paid && <LockIcon />}
            {reportBusy === "header" ? "Preparing…" : "Report PDF"}
          </button>
        </div>
      </header>

      {showLocked && !paid && <LockedNotice />}
      {reportError && (
        <ul className="lf-notices"><li className="lf-notice lf-notice--bad" role="alert">{reportError}</li></ul>
      )}

      {error && (
        <div className="lf-card adm-message" role="alert">
          <p>Couldn't load your live fund: {error}</p>
          <button type="button" className="adm-btn adm-btn--outline" onClick={() => setReloadKey((n) => n + 1)}>Try again</button>
        </div>
      )}
      {!error && !data && <LiveSkeleton />}
      {!error && data && !hasAny && <EmptyState />}
      {!error && data && hasAny && (
        <div className={stale ? "lf-loading" : ""}>
          <div className="lf-card">
          <div className="lf-accounts">
            {MODES.map((m) => (
              <AccountCard key={m.key} account={data.accounts[m.key]} active={mode === m.key} onSelect={() => update({ mode: m.key })} />
            ))}
          </div>

          <div className="adm-toolbar">
            <div className="adm-segmented" role="group" aria-label="Period">
              {RANGES.map((r) => (
                <button key={r.key} type="button" aria-pressed={range === r.key}
                        className={range === r.key ? "is-active" : ""} onClick={() => update({ range: r.key })}>
                  {r.label}
                </button>
              ))}
            </div>
            {s && (
              <span className="adm-note">
                {longDay(s.startDay)} – {longDay(s.endDay)} · {s.days} trading day{s.days === 1 ? "" : "s"} · amounts in {currency}
              </span>
            )}
          </div>
          </div>

          <Notices account={account} />

          {!s ? (
            <div className="lf-card adm-message"><p>No account values for the {mode} account yet: they're recorded after each run.</p></div>
          ) : (
            <>
              <div className="lf-card">
              <div className="adm-kpis">
                <KpiCard label="Account value" value={money(s.nav, currency)} spark={navSpark} sparkColor={LIGHT.account}
                         sub={<><span className={tone(s.change)}>{signedMoney(s.change, currency)}</span> in this period</>} />
                <KpiCard label="Return" value={<span className={tone(s.totalReturn)}>{pct(s.totalReturn)}</span>}
                         sub={s.model ? <>model {pct(s.model.totalReturn)} · <span className={tone(s.excessReturn)}>{pct(s.excessReturn)}</span> vs model</> : "time-weighted"} />
                <KpiCard label="Annualized" value={pct(s.annualizedReturn)}
                         sub={s.annualizedReturn == null ? "from 90 days of history" : `volatility ${share(s.volatility, 1)}`} />
                <KpiCard label="Sharpe" value={ratio(s.sharpe)} sub={s.sharpe == null ? "from 20 trading days" : `Sortino ${ratio(s.sortino)} · Calmar ${ratio(s.calmar)}`} />
                <KpiCard label="Max drawdown" value={<span className={s.maxDrawdown < 0 ? "adm-text-danger" : ""}>{pct(s.maxDrawdown)}</span>}
                         sub={`now ${pct(s.currentDrawdown)}`} />
                <KpiCard label="Positive days" value={share(s.positiveDays, 0)}
                         sub={s.bestDay ? <>best <span className="adm-text-accent">{pct(s.bestDay.ret)}</span> · worst <span className="adm-text-danger">{pct(s.worstDay.ret)}</span></> : null} />
                <KpiCard label="Invested" value={share(s.investedRatio, 0)} sub={`cash ${money(s.cash, currency)} (${share(s.cashRatio, 0)})`} />
                <KpiCard label="Open positions" value={s.openPositions}
                         sub={s.largestPosition ? `largest ${s.largestPosition.ticker} ${share(s.largestPosition.weight)}` : null} />
                <KpiCard label="Unrealized P&L" value={<span className={tone(s.unrealizedPnl)}>{signedMoney(s.unrealizedPnl, currency)}</span>}
                         sub="open positions, at IBKR's prices" />
                <KpiCard label="Realized P&L" value={<span className={tone(s.closed.realizedPnl)}>{signedMoney(s.closed.count ? s.closed.realizedPnl : null, currency)}</span>}
                         sub={s.closed.count ? `${s.closed.count} closed · ${share(s.closed.winRate, 0)} won · profit factor ${s.closed.profitFactor == null ? "—" : s.closed.profitFactor.toFixed(2)}` : "no trade closed in this period"} />
                <KpiCard label="Fill rate" value={share(s.trading.fillRate, 0)} sub={`${s.trading.orders} order${s.trading.orders === 1 ? "" : "s"} · turnover ${s.trading.turnover == null ? "—" : `${s.trading.turnover.toFixed(2)}×`}`} />
                <KpiCard label="Trading costs" value={money(s.trading.commissions, currency, 2)}
                         sub={`fees ${s.trading.commissionBps == null ? "—" : `${s.trading.commissionBps.toFixed(1)} bps`} · shortfall ${bps(s.trading.shortfallBps)}`} />
              </div>
              </div>

              <div className="lf-card lf-cols lf-cols--wide">
                <div>
                  <h2 className="adm-panel__title">Performance <small>cumulative return{s.model ? " · model fund over the same days" : ""}</small></h2>
                  <div className="adm-chart lf-chart--tall"><PerformanceChart series={series} /></div>
                </div>
                <div>
                  <h2 className="adm-panel__title">Risk &amp; return</h2>
                  <StatsTable s={s} />
                </div>
              </div>

              <div className="lf-card lf-cols">
                <div>
                  <h2 className="adm-panel__title">Drawdown <small>fall from the period's high</small></h2>
                  <div className="adm-chart"><DrawdownChart series={series} /></div>
                </div>
                <div>
                  <h2 className="adm-panel__title">Daily returns</h2>
                  <div className="adm-chart"><DailyReturnsChart series={series} /></div>
                </div>
              </div>

              <div className="lf-card lf-cols lf-cols--wide">
                <div>
                  <h2 className="adm-panel__title">Allocation <small>{data.modelWeights ? "share of the account vs the model fund today" : "share of the account"}</small></h2>
                  {allocation.length
                    ? <div className="adm-chart" style={{ height: Math.max(240, allocation.length * 24 + 50) }}><AllocationChart rows={allocation} /></div>
                    : <p className="adm-note">No open positions.</p>}
                </div>
                <div>
                  <h2 className="adm-panel__title">Execution <small>orders sent in this period</small></h2>
                  <Execution t={s.trading} currency={currency} />
                </div>
              </div>
            </>
          )}

          <div className="lf-card adm-details">
            <div className="adm-details__head">
              <nav className="adm-tabs adm-tabs--small" aria-label="Lists">
                {TABLES.map((t) => (
                  <button key={t.key} type="button" aria-current={table === t.key ? "page" : undefined}
                          className={table === t.key ? "is-active" : ""} onClick={() => update({ table: t.key })}>
                    {t.label}
                    <span className="lf-tab-count">{t.key === "trades" ? data.trades.length : t.key === "daily" ? data.daily.length
                      : t.key === "positions" ? data.positions.length : t.key === "closed" ? data.closedTrades.length
                        : data.statements?.length ?? 0}</span>
                  </button>
                ))}
              </nav>
            </div>
            {table === "trades" && <TradesTable data={data} />}
            {table === "daily" && <DailyTable data={data} />}
            {table === "positions" && <PositionsTable data={data} />}
            {table === "closed" && <ClosedTable data={data} />}
            {table === "statements" && (
              <StatementsTable data={data} account={account} paid={paid} busy={reportBusy} onOpen={openReport} />
            )}
          </div>

          <p className="adm-note adm-footnote">
            Returns are time-weighted from the daily account value reported by IBKR and assume no deposits or withdrawals;
            ratios use 252 trading days and a risk-free rate of 0. Updated {formatRelative(data.generatedAt)}.
          </p>
        </div>
      )}
    </div>
  );
};

export default LiveFund;
